import { Box, Card, CardContent, CircularProgress, LinearProgress, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { supabase } from '../../supabaseClient'
import { KU_TYPES } from '../../types'

interface AnalyticsData {
  totalKUs: number
  acceptedKUs: number
  draftKUs: number
  typeBreakdown: Record<string, number>
  passBreakdown: Record<string, number>
  providerBreakdown: Record<string, number>
  standardsCovered: number
  avgVoteScore: number
}

export default function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const { data: kos } = await supabase
        .from('knowledge_units')
        .select('id, status, proposed_type, pass_id, standard_id, votes(*), run:runs(provider)')

      if (!kos) {
        setLoading(false)
        return
      }

      const typeBreakdown: Record<string, number> = {}
      const passBreakdown: Record<string, number> = {}
      const providerBreakdown: Record<string, number> = {}
      let totalScore = 0
      let totalVotes = 0

      kos.forEach((ko: any) => {
        // Type
        const t = ko.proposed_type || 'unclear'
        typeBreakdown[t] = (typeBreakdown[t] || 0) + 1
        // Pass
        passBreakdown[ko.pass_id] = (passBreakdown[ko.pass_id] || 0) + 1
        // Provider
        const prov = ko.run?.provider || 'unknown'
        providerBreakdown[prov] = (providerBreakdown[prov] || 0) + 1
        // Votes
        ;(ko.votes || []).forEach((v: any) => {
          totalScore += v.score
          totalVotes++
        })
      })

      setData({
        totalKUs: kos.length,
        acceptedKUs: kos.filter((k: any) => k.status === 'accepted').length,
        draftKUs: kos.filter((k: any) => k.status === 'draft').length,
        typeBreakdown,
        passBreakdown,
        providerBreakdown,
        standardsCovered: new Set(kos.map((k: any) => k.standard_id)).size,
        avgVoteScore: totalVotes > 0 ? Math.round((totalScore / totalVotes) * 10) / 10 : 0,
      })
      setLoading(false)
    }
    load()
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
          Analytics
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Knowledge extraction research metrics
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : !data ? null : (
          <>
            {/* Summary cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2 }}>
              {[
                { label: 'Total KUs', value: data.totalKUs, color: '#1e3a5f' },
                { label: 'Standards', value: data.standardsCovered, color: '#7c3aed' },
                { label: 'Accepted', value: data.acceptedKUs, color: '#16a34a' },
                { label: 'Avg Vote', value: data.avgVoteScore || '—', color: '#f59e0b' },
              ].map((s) => (
                <Card key={s.label} variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: s.color }}>
                      {s.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      {s.label}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Coverage */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: 10,
                    letterSpacing: '0.06em',
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  Acceptance Rate
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={data.totalKUs > 0 ? (data.acceptedKUs / data.totalKUs) * 100 : 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': { bgcolor: '#16a34a', borderRadius: 4 },
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, display: 'block', mt: 0.5 }}>
                  {data.acceptedKUs} of {data.totalKUs} KUs accepted (
                  {data.totalKUs > 0 ? Math.round((data.acceptedKUs / data.totalKUs) * 100) : 0}%)
                </Typography>
              </CardContent>
            </Card>

            {/* Type distribution */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: 10,
                    letterSpacing: '0.06em',
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  Type Distribution
                </Typography>
                {KU_TYPES.map((t) => {
                  const count = data.typeBreakdown[t.value] || 0
                  const pct = data.totalKUs > 0 ? (count / data.totalKUs) * 100 : 0
                  return (
                    <Box key={t.value} sx={{ mb: 1.25 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: t.color, fontSize: 11 }}>
                          {t.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          {count} ({Math.round(pct)}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': { bgcolor: t.color, borderRadius: 3 },
                        }}
                      />
                    </Box>
                  )
                })}
              </CardContent>
            </Card>

            {/* Pass breakdown */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: 10,
                    letterSpacing: '0.06em',
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  By Pass
                </Typography>
                {[
                  { id: 'pass1', label: 'Requirement', color: '#0369a1' },
                  { id: 'pass2', label: 'Content', color: '#7c3aed' },
                  { id: 'pass3', label: 'Knowledge', color: '#065f46' },
                ].map((p) => {
                  const count = data.passBreakdown[p.id] || 0
                  const pct = data.totalKUs > 0 ? (count / data.totalKUs) * 100 : 0
                  return (
                    <Box key={p.id} sx={{ mb: 1.25 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: p.color, fontSize: 11 }}>
                          {p.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                          {count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': { bgcolor: p.color, borderRadius: 3 },
                        }}
                      />
                    </Box>
                  )
                })}
              </CardContent>
            </Card>

            {/* Provider breakdown */}
            {Object.keys(data.providerBreakdown).length > 0 && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: 10,
                      letterSpacing: '0.06em',
                      display: 'block',
                      mb: 1.5,
                    }}
                  >
                    By Provider
                  </Typography>
                  {Object.entries(data.providerBreakdown).map(([provider, count]) => (
                    <Box key={provider} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, textTransform: 'capitalize' }}>
                        {provider}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                        {count} KUs
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
